import React, { useState } from 'react';
import { Card, Typography, Divider, Button, Input, Modal, Radio, Select, Tag, message } from 'antd';
import { SoundFilled, SoundOutlined, LockOutlined, CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";
import { ReactComponent as EditIcon } from '../../assets/icon/edit.svg';
import { ReactComponent as CopyIcon } from '../../assets/icon/copy.svg';
import { ReactComponent as CorrectIcon } from '../../assets/icon/check.svg';
import { ReactComponent as IncorrectIcon } from '../../assets/icon/close.svg';
import InfoCardFooter, {
    InfoCardCounter,
    InfoCardIconButton,
} from "../InfoCardFooter/InfoCardFooter";
import useTranslation from "../../hooks/useTranslation";
import useAudioCache from "../../hooks/useAudioCache";
import wordClient from "../../client/WordClient";
import './WordInfoCard.css';

const { Text } = Typography;

/**
 * Переводы определения списком. Старые ответы сервера списка не содержат — у них
 * одно поле translation, и оно показывается как канонический перевод.
 */
const translationsOf = (definition) => {
    if (definition.translations?.length > 0) {
        return definition.translations;
    }
    return definition.translation ? [{ text: definition.translation, source: 'CANONICAL' }] : [];
};

const WordInfoCard = ({ word, onUpdateWord, locale }) => {
    const { t } = useTranslation();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [openExamples, setOpenExamples] = useState({}); // <--- Состояние для раскрытия примеров
    // Форма «добавить свой пример»: к какому определению (по индексу — id у старых
    // слов может отсутствовать), текст и перевод.
    const [newExampleDefinitionIndex, setNewExampleDefinitionIndex] = useState(0);
    const [newExampleSentence, setNewExampleSentence] = useState('');
    const [newExampleTranslation, setNewExampleTranslation] = useState('');
    const [savingExample, setSavingExample] = useState(false);
    // Форма «мой перевод»: только канонические определения с id — override
    // адресует определение статьи. Пустой текст при сохранении снимает override.
    const canonicalDefinitions = (word.wordDefinition || [])
        .map((def, index) => ({ def, index }))
        .filter(({ def }) => def.id && def.source !== 'USER');
    const [translationDefinitionId, setTranslationDefinitionId] = useState(
        canonicalDefinitions.length > 0 ? canonicalDefinitions[0].def.id : null);
    const [ownTranslation, setOwnTranslation] = useState('');
    const [overrideMode, setOverrideMode] = useState('APPEND');
    const [savingTranslation, setSavingTranslation] = useState(false);

    // Кэш и проигрывание озвучки вынесены в общий хук: той же логикой
    // пользуются карточки практики. Поведение не менялось.
    const { playAudio, prefetchAudio } = useAudioCache(
        `${word.headword}|${word.partOfSpeech}|${locale.code}`, locale.code);

    // Играть можно и по ALLOWED, и по NOT_READY: во втором случае файла заведомо нет,
    // и нажатие закажет синтез после подтверждения (useAudioCache). Контурный динамик
    // показывает, что озвучку ещё предстоит создать.
    const canPlay = (audio) => audio?.access === 'ALLOWED' || audio?.access === 'NOT_READY';
    const speakerIcon = (audio, fontSize) => (audio?.access === 'NOT_READY'
        ? <SoundOutlined style={{fontSize, color: '#838383'}}/>
        : <SoundFilled style={{fontSize, color: '#838383'}}/>);

    // Отображение модального окна
    const showModal = () => {
        setIsModalVisible(true);
    };

    // Закрытие модального окна
    const handleCancel = () => {
        setIsModalVisible(false);
    };

    /**
     * Свой пример уходит на сервер без id: id и происхождение USER присваивает сервер,
     * и только у таких примеров озвучка синтезируется лениво под квотой.
     */
    /**
     * Запрос обновления собирается из текущего слова целиком: PUT замещает документ,
     * и забытые здесь overrides или свои примеры пропали бы при любом сохранении.
     */
    const buildUpdateRequest = ({ exampleForDefinitionIndex } = {}) => ({
        deckId: word.deckId,
        canonicalWordId: word.canonicalWordId || null,
        headword: word.headword,
        phonetic: word.phonetic,
        partOfSpeech: word.partOfSpeech,
        overrides: word.overrides || null,
        wordDefinition: (word.wordDefinition || []).map((def, index) => ({
            id: def.id,
            definition: def.definition,
            translation: def.translation,
            // Происхождение возвращаем серверу, как и у примеров: не вернуть — значит
            // сказать, что определение каноническое, и своё определение получило бы
            // ссылку на озвучку, которой не существует.
            source: def.source,
            examples: [
                ...(def.examples || []).map((ex) => ({
                    id: ex.id,
                    sentence: ex.sentence,
                    translation: ex.translation,
                    source: ex.source,
                })),
                ...(index === exampleForDefinitionIndex
                    ? [{ sentence: newExampleSentence.trim(), translation: newExampleTranslation.trim() }]
                    : []),
            ],
        })),
    });

    const handleAddExample = async () => {
        setSavingExample(true);
        try {
            const request = buildUpdateRequest({ exampleForDefinitionIndex: newExampleDefinitionIndex });
            const updatedWord = await wordClient.updateWord(word.id, request);
            if (onUpdateWord) {
                onUpdateWord(updatedWord);
            }
            message.success(t('word.exampleAdded'));
            setNewExampleSentence('');
            setNewExampleTranslation('');
            setIsModalVisible(false);
        } catch (error) {
            console.error('Не удалось добавить пример:', error);
            message.error(t('word.exampleAddFailed'));
        } finally {
            setSavingExample(false);
        }
    };

    /** Свой перевод: «вместо или вместе» решает mode; пустой текст снимает override. */
    const handleSaveTranslation = async () => {
        setSavingTranslation(true);
        try {
            const request = buildUpdateRequest();
            const overrides = { ...word.overrides };
            if (ownTranslation.trim()) {
                overrides[translationDefinitionId] = { mode: overrideMode, translation: ownTranslation.trim() };
            } else {
                delete overrides[translationDefinitionId];
            }
            request.overrides = Object.keys(overrides).length > 0 ? overrides : null;
            const updatedWord = await wordClient.updateWord(word.id, request);
            if (onUpdateWord) {
                onUpdateWord(updatedWord);
            }
            message.success(t('word.translationSaved'));
            setIsModalVisible(false);
        } catch (error) {
            console.error('Не удалось сохранить перевод:', error);
            message.error(t('word.translationSaveFailed'));
        } finally {
            setSavingTranslation(false);
        }
    };

    const toggleExamples = (index) => {
        setOpenExamples((prevState) => ({
            ...prevState,
            [index]: !prevState[index],
        }));
    };

    // Слово копируем в том же виде, в каком оно показано: заголовок, затем
    // определения с переводом и примерами. Раскрыты примеры на экране или нет,
    // на буфер обмена не влияет — копируется вся статья.
    const buildCopiedWord = () => {
        const header = [word.headword, word.partOfSpeech, word.phonetic ? `/${word.phonetic}/` : '']
            .filter(Boolean)
            .join(' ');

        const definitions = (word.wordDefinition || []).map((definition) => [
            [definition.definition, definition.translation].filter(Boolean).join(' — '),
            ...(definition.examples || []).map((example) =>
                [example.sentence, example.translation].filter(Boolean).join(' — ')),
        ].join('\n'));

        return [header, ...definitions].filter(Boolean).join('\n\n');
    };

    const handleCopy = () => {
        navigator.clipboard
            .writeText(buildCopiedWord())
            .then(() => {
                message.success(t('common.copied'));
            })
            .catch((error) => {
                message.error(t('common.copyFailed'));
                console.error(error);
            });
    };

    return (
        <>
            <Card
                key={word.id}
                className="wordInfoCard"
                bordered={false}
                styles={{ body: { padding: 0 } }}
            >
                <div style={{ padding: '16px 16px 0 16px' }}>
                    {/* Word Headword with Speaker Icon */}
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        {!word.phonetic && canPlay(word.audio) && (
                            <button
                                type="button"
                                aria-label={t('common.listen')}
                                className="wordInfoCard__speaker"
                                style={{ marginRight: '8px' }}
                                onPointerDown={() => prefetchAudio(word.audio.ref, word.audio.access)}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    playAudio(word.audio.ref, word.audio.access);
                                }}
                            >
                                {speakerIcon(word.audio, '0.9em')}
                            </button>
                        )}
                        <div style={{
                            display: 'flex',
                            alignItems: 'baseline'
                        }}>
                            <Text strong style={{fontSize: '2em', color: '#027b83'}}>{word.headword}</Text>
                            <Text strong style={{fontSize: '1.2em', marginLeft: '8px'}}>{word.partOfSpeech}</Text>
                        </div>
                    </div>

                    {/* Transcription with Sound Icon */}
                    <div style={{display: 'flex', justifyContent: 'left', alignItems: 'center', marginTop: '0px'}}>


                        {word.phonetic && canPlay(word.audio) && (

                            <button
                                type="button"
                                aria-label={t('common.listen')}
                                className="wordInfoCard__speaker"
                                onPointerDown={() => prefetchAudio(word.audio.ref, word.audio.access)}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    playAudio(word.audio.ref, word.audio.access);
                                }}
                            >
                                {speakerIcon(word.audio, '0.9em')}
                            </button>)}
                            <Text style={{fontSize: '1.5em', marginLeft: '8px'}}>
                                {word.phonetic ? `/${word.phonetic}/` : ''}
                            </Text>

                        </div>

                        <Divider style={{margin: '8px 0'}}/>

                        {word.wordDefinition.map((def, index) => (
                            <div key={def.id} style={{marginBottom: '16px'}}>
                                <button type="button" aria-expanded={Boolean(openExamples[index])} onClick={() => toggleExamples(index)}
                                     style={{cursor: 'pointer', display: 'flex', alignItems: 'center', background: 'none', border: 'none', padding: 0, font: 'inherit', textAlign: 'left', width: '100%'}}>
                                    {openExamples[index] ? <CaretUpOutlined style={{color: '#838383'}}/> :
                                        <CaretDownOutlined style={{color: '#838383'}}/>}
                                    <Text strong style={{marginLeft: '8px'}}>{def.definition}</Text>
                                </button>
                                {/* Переводы с происхождением: REPLACE — только свой, APPEND — оба.
                                    Старые ответы без списка показываются по одиночному полю. */}
                                {translationsOf(def).map((translation, translationIndex) => (
                                    <span key={`${translation.source}:${translation.text}`}>
                                        <Text italic strong style={{
                                            marginLeft: translationIndex === 0 ? '36px' : '8px',
                                            color: translation.source === 'USER' ? '#0958d9' : '#ee6002',
                                        }}>{translation.text}</Text>
                                        {translation.source === 'USER' && (
                                            <Tag style={{ marginLeft: '4px' }} color="blue">
                                                {t('word.myTranslation')}
                                            </Tag>
                                        )}
                                    </span>
                                ))}

                                {openExamples[index] && (
                                    <div style={{marginTop: '10px', marginLeft: '16px'}}>
                                        {def.examples.map((example, exampleIndex) => (
                                            <div key={example.id} style={{marginBottom: '8px'}}>
                                                {/* Вердикт политики приходит в данных ответа: замок вместо динамика —
                                                    решение сервера, клиент тариф не угадывает. */}
                                                {canPlay(example.audio) && (
                                                <button
                                                    type="button"
                                                    aria-label={t('common.listenExample')}
                                                    className="wordInfoCard__speaker"
                                                    style={{ marginRight: '8px' }}
                                                    onPointerDown={() => prefetchAudio(example.audio.ref, example.audio.access)}
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        playAudio(example.audio.ref, example.audio.access);
                                                    }}
                                                >
                                                    {speakerIcon(example.audio, '0.6em')}
                                                </button>
                                                )}
                                                {example.audio?.access === 'PLAN_REQUIRED' && (
                                                <button
                                                    type="button"
                                                    aria-label={t('common.exampleAudioPremium')}
                                                    className="wordInfoCard__speaker"
                                                    style={{ marginRight: '8px' }}
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        message.info(t('common.exampleAudioPremium'));
                                                    }}
                                                >
                                                    <LockOutlined style={{fontSize: '0.6em', color: '#838383'}}/>
                                                </button>
                                                )}
                                                <Text>{example.sentence}</Text>
                                                {example.source === 'USER' && (
                                                    <Tag style={{ marginLeft: '8px' }} color="cyan">
                                                        {t('word.myExample')}
                                                    </Tag>
                                                )}
                                                <br/>
                                                <Text  type="secondary" italic style={{marginLeft: '32px'}}>{example.translation}</Text>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}

                        <Divider style={{margin: '2px 0'}}/>

                        {/* Счётчики ответов и действия над словом */}
                        <InfoCardFooter>
                            <InfoCardCounter
                                icon={CorrectIcon}
                                title={t('common.correctAnswers')}
                                value={word.correctAnswerCount}
                            />
                            <InfoCardCounter
                                icon={IncorrectIcon}
                                title={t('common.incorrectAnswers')}
                                value={word.incorrectAnswerCount}
                            />
                            <InfoCardIconButton icon={CopyIcon} title={t('common.copy')} onClick={handleCopy} />
                            <InfoCardIconButton icon={EditIcon} title={t('common.edit')} onClick={showModal} />
                        </InfoCardFooter>
                    </div>
            </Card>

            {/* Пока форма умеет одно: добавить свой пример к выбранному определению. */}
            <Modal
                title={`${t('common.edit')}: ${word.headword}`}
                open={isModalVisible}
                onCancel={handleCancel}
                footer={null}
                width="60%"
                style={{ top: 16 }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Select
                        value={newExampleDefinitionIndex}
                        onChange={setNewExampleDefinitionIndex}
                        options={(word.wordDefinition || []).map((def, index) => ({
                            value: index,
                            label: def.definition,
                        }))}
                    />
                    <Input
                        placeholder={t('word.newExampleSentence')}
                        value={newExampleSentence}
                        onChange={(event) => setNewExampleSentence(event.target.value)}
                    />
                    <Input
                        placeholder={t('word.newExampleTranslation')}
                        value={newExampleTranslation}
                        onChange={(event) => setNewExampleTranslation(event.target.value)}
                    />
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <Button onClick={handleCancel}>{t('common.cancel')}</Button>
                        <Button
                            type="primary"
                            loading={savingExample}
                            disabled={!newExampleSentence.trim()}
                            onClick={handleAddExample}
                        >
                            {t('common.save')}
                        </Button>
                    </div>

                    {canonicalDefinitions.length > 0 && (
                        <>
                            <Divider style={{ margin: '12px 0' }} />
                            {/* Свой перевод к каноническому определению: вместо или вместе. */}
                            <Select
                                value={translationDefinitionId}
                                onChange={(definitionId) => {
                                    setTranslationDefinitionId(definitionId);
                                    setOwnTranslation(word.overrides?.[definitionId]?.translation || '');
                                    setOverrideMode(word.overrides?.[definitionId]?.mode || 'APPEND');
                                }}
                                options={canonicalDefinitions.map(({ def }) => ({
                                    value: def.id,
                                    label: def.definition,
                                }))}
                            />
                            <Input
                                placeholder={t('word.newTranslation')}
                                value={ownTranslation}
                                onChange={(event) => setOwnTranslation(event.target.value)}
                            />
                            <Radio.Group
                                value={overrideMode}
                                onChange={(event) => setOverrideMode(event.target.value)}
                                options={[
                                    { value: 'APPEND', label: t('word.translationTogether') },
                                    { value: 'REPLACE', label: t('word.translationInstead') },
                                ]}
                            />
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                <Button
                                    type="primary"
                                    loading={savingTranslation}
                                    disabled={!ownTranslation.trim() && !word.overrides?.[translationDefinitionId]}
                                    onClick={handleSaveTranslation}
                                >
                                    {t('common.save')}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </Modal>
        </>
    );
};

export default WordInfoCard;
